<template>
  <div class="component">
    <div class="alert alert-info mb-4">
      <div class="inner">
        <div class="ampoule">
          <svg width="14" height="20" viewBox="0 0 14 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M7 0C3.14 0 0 3.14 0 7C0 9.38 1.19 11.47 3 12.74V15C3 15.55 3.45 16 4 16H10C10.55 16 11 15.55 11 15V12.74C12.81 11.47 14 9.38 14 7C14 3.14 10.86 0 7 0ZM4 19C4 19.55 4.45 20 5 20H9C9.55 20 10 19.55 10 19V18H4V19ZM9 11.7L9.85 11.1C11.2 10.16 12 8.63 12 7C12 4.24 9.76 2 7 2C4.24 2 2 4.24 2 7C2 8.63 2.8 10.16 4.15 11.1L5 11.7V14H9V11.7Z" fill="white"></path>
          </svg>
        </div>
        <p>
          Le tableau au ci-contre présente une <strong>estimation des garanties</strong> sur la base des éléments communiqués.<br />
          Les paramètres de calcul utilisés sont des moyennes.
        </p>
        <p>
          Les montants affichés sont exprimés en <strong>brut</strong>, c'est-à-dire <strong>avant toute déduction</strong> des cotisations sociales ou de l'impôt.<br />
          Ils sont également calculés <strong>sans appliquer les plafonds légaux</strong>, qui limitent toujours le niveau des prestations à hauteur de la rémunération nette réellement perçue.<br />
          Ces chiffres ont donc pour seul objectif de vous donner <strong>un ordre de grandeur clair et compréhensible</strong>, afin de comparer les différentes options possibles.
        </p>
        <p>
          Pour les agents qui sont en poste dans l’un des territoires suivants : Nouvelle-Calédonie, Wallis et Futuna, Polynésie française, Saint-Pierre-et-Miquelon : l’indemnisation dans le cadre de votre couverture prévoyance complète, en fonction de la configuration que vous avez choisie (socle ou option), le traitement et les primes ; elle ne couvre pas l’indexation. Pour estimer vos garanties avec le présent outil : nous vous invitons à renseigner votre rémunération hors indexation ; pour connaitre le montant total de votre indemnisation (statutaire + couverture prévoyance), il vous faut ajouter le montant de l’indexation partiellement maintenue par l’employeur. 
        </p>
        <p>
          En cas de sinistre, chaque situation personnelle devra être analysée afin de déterminer le montant exact de l'indemnisation due au titre du contrat.<br />
          Les données renseignées dans le simulateur ne seront pas enregistrées, elles servent uniquement à vous proposer une simulation à titre informatif.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import useVuelidate from "@vuelidate/core";
import { minValue, required } from "@vuelidate/validators";

import objectHelper from "@/classes/helpers/objectHelper";
import UserInformations from "@/classes/dto/userInformations";

const greaterThanZero = (value) => value > 0;

export default {
  name: "UserInputForm",
  emits: ["compute", "print"],
  data() {
    return {
      objectHelper,
      personalInformationValues: new UserInformations(),
      buttonComputeClickedAtLeastOneTime: false,
      showPrintButton: false,
      showTrancheAgeTooltip: false,
    };
  },
  setup: () => {
    return { v$: useVuelidate() };
  },
  created() {},
  validations() {
    return {
      personalInformationValues: {
        salaireBrut: { required, greaterThanZero },
        primes: { required, minValue: minValue(0) },
      },
    };
  },
  mounted() {
    if (window.location.hostname == "localhost") {
      setTimeout(() => {
        this.buttonComputeClickedAtLeastOneTime = true;
        this.computeUserInput();
      }, 1000);
    }
  },
  computed: {
    formatedSalaire() {
      return this.personalInformationValues.salaireBrut.toLocaleString("fr-FR", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 0,
      });
    },
    formatedPrimes() {
      return this.personalInformationValues.primes.toLocaleString("fr-FR", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 0,
      });
    },
  },
  methods: {
    print() {
      this.$emit("print");
    },
    updateSalaireBrut(event) {
      const value = event.target.value.replace(/[^\d]/g, "");
      this.personalInformationValues.salaireBrut = parseFloat(value) || 0;
    },
    updatePrimes(event) {
      const value = event.target.value.replace(/[^\d]/g, "");
      this.personalInformationValues.primes = parseFloat(value) || 0;
    },
    async computeUserInput(event, buttonWasClicked = false) {
      if (buttonWasClicked) {
        this.buttonComputeClickedAtLeastOneTime = true;
      }

      if (this.buttonComputeClickedAtLeastOneTime && (await this.v$.$validate()) === true) {
        this.$emit("compute", this.personalInformationValues);
        this.showPrintButton = true;
      }
    },
    manageTooltip() {},
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/_variables.scss";

.component {
  $sideMargin: 5rem;

  form {
    padding: 1.5rem 0;
    margin: 0 $sideMargin;
    border-radius: 5px;
    color: $secondary;
    background-color: white;

    @media (max-width: $screen-md-max) {
      padding: 0.5rem 0;
    }

    .title {
      font: normal normal normal 26px/38px "csb";
      text-align: center;

      @media (max-width: $screen-md-max) {
        padding: 0 1rem;
        margin-top: 0.5rem;
        line-height: 30px;
      }
    }

    .form-group {
      padding: 0 $sideMargin;
    }
  }

  @media (max-width: $screen-md-max) {
    $sideMargin: 1rem;

    form {
      margin: 0 $sideMargin;

      .form-group {
        padding: 0 $sideMargin;
      }
    }
  }

  .button-container {
    margin: 0;
    margin-right: 5rem;
    display: flex;

    @media (max-width: $screen-md-max) {
      $sideMargin: 1rem;
      margin: 0 $sideMargin;
    }

    button {
      $size: 48px;
      height: $size;
      text-align: left;
      width: $size;

      &:not(.hidden-print) {
        width: 100% !important;

        &.btn-primary {
          font: normal normal normal 16px/22px "csb";
          letter-spacing: 0px;
          color: white;
          border-color: white;
          background-color: transparent;
        }

        img {
          float: right;
        }
      }

      &.hidden-print {
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 0.5rem;
        border-color: white;
        background-color: transparent;

        img {
          $ratio: 0.7;
          max-width: calc($size * $ratio);
          max-height: calc($size * $ratio);
        }
      }
    }
  }
}
</style>
